<template>
  <v-card :class="{ 'cardheight': $vuetify.breakpoint.mdAndUp }" flat>
    <v-container v-if="isReady">
      <v-card flat class="mb-2">
        <v-card-title primary-title class="pt-0 pb-0">
          <v-layout align-center>
            <span v-if="typeLabel" class="title">{{ typeLabel }}</span>
            <v-chip
              :color="statusColor"
              text-color="white"
              small
              class="ma-0 ml-2"
            >
              {{ statusLabel }}
            </v-chip>
          </v-layout>
          <div class="title warning--text font-weight-bold">
            {{ bookingPrice
            }}<span v-if="!isPricePaid" class="subheading">/h</span>
          </div>
        </v-card-title>

        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 sm6 class="mb-2">
              <div class="caption grey--text text--darken-1">
                {{
                  booking.isFacility
                    ? $t('global.placeholder.start')
                    : $t('global.placeholder.from')
                }}
              </div>
              <div class="subheading">{{ startFormatted }}</div>
            </v-flex>

            <v-flex v-if="showEnd" xs12 sm6 class="mb-2">
              <div class="caption grey--text text--darken-1">
                {{ $t('global.placeholder.to') }}
              </div>
              <div class="subheading">{{ endFormatted }}</div>
            </v-flex>
          </v-layout>

          <v-layout row wrap>
            <v-flex v-if="hasAssignation" xs12 sm4 class="mb-2">
              <div
                class="caption grey--text text--darken-1"
                v-html="$t('reservation.detail.spotAssigned')"
              ></div>
              <div class="subheading">{{ booking.spotName }}</div>
            </v-flex>

            <v-flex v-if="booking.plate" xs12 sm4 class="mb-2">
              <div
                class="caption grey--text text--darken-1"
                v-html="$t('global.plate')"
              ></div>
              <div class="subheading">{{ booking.plate }}</div>
            </v-flex>

            <v-flex xs12 sm4 class="mb-2">
              <div
                class="caption grey--text text--darken-1"
                v-html="$t('global.id')"
              ></div>
              <div class="subheading">
                {{ booking.externalId || booking.id }}
              </div>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>

      <v-card v-if="showClosingTime" flat class="mb-2">
        <v-card-title primary-title class="pt-2 pb-0">
          <div>
            <div class="title">{{ $t('global.closing.time') }}</div>
            <div class="subheading grey--text text--darken-1">
              {{ booking.closingTime | formatDateAsUtc($i18n.locale) }}
            </div>
          </div>
        </v-card-title>

        <v-card-text v-html="closingText"></v-card-text>
      </v-card>

      <v-card v-if="showMaxAvailability" flat class="mb-2">
        <v-card-title primary-title class="pt-2 pb-0">
          <div>
            <div class="title">{{ $t('global.max.availability') }}</div>
            <div class="subheading grey--text text--darken-1">
              {{ booking.maxAvailabilityLocal | formatDateAsUtc($i18n.locale) }}
            </div>
          </div>
        </v-card-title>

        <v-card-text
          v-html="
            $t('reservation.warning.max.availability', {
              maxAvailability: maxAvailabilityFormatted,
              extraPrice: booking.minuteExtraPriceWithCurrency
            })
          "
        ></v-card-text>
      </v-card>

      <slot></slot>
    </v-container>
  </v-card>
</template>

<script>
import moment from 'moment';
import { setTimeout } from 'timers';
export default {
  name: 'BookingShow',
  props: {
    booking: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isReady: false,
    };
  },
  computed: {
    isModifiable() {
      return !(
        this.isSubscription ||
        this.booking.status === 'PAID' ||
        this.booking.status === 'IN_USE' ||
        this.booking.isFacility
      );
    },
    typeLabel() {
      // En un servicio el propio chip ya dice de que se trata ("Servicio
      // pagado"), asi que no hace falta anteponerle nada.
      if (this.booking.isFacility) {
        return '';
      }
      return this.isSubscription
        ? this.$i18n.t('global.subscription')
        : this.$i18n.t('global.bookings');
    },
    statusLabel() {
      if (typeof this.booking.status === 'undefined') {
        return '';
      }
      if (this.booking.isFacility) {
        return this.$i18n.t('facility.detail.status.title');
      }
      if (this.isSubscription) {
        return this.$i18n.t(`status.SUBSCRIPTION_${this.booking.status}`);
      }
      return this.$i18n.t(`status.BOOKING_${this.booking.status}`);
    },
    showMaxAvailability() {
      if (this.isSubscription) {
        return false;
      }

      if (this.booking.maxAvailabilityLocal === 'undefined') {
        return false;
      }

      return moment(this.booking.endLocal)
        .utc()
        .add(1, 'day')
        .isAfter(this.booking.maxAvailabilityLocal);
    },
    showClosingTime() {
      return (
        this.booking.blockAccessOnClosingTime === true &&
        !!this.booking.closingTime
      );
    },
    showReopeningTime() {
      return this.showClosingTime && !!this.booking.reopeningTime;
    },
    showEnd() {
      return (
        (this.isSubscription || this.booking.status === 'PAID') &&
        !this.booking.isFacility
      );
    },
    isSubscription() {
      return this.booking.type === 'SUBSCRIPTION';
    },
    statusColor() {
      // Mismo criterio que getEventClassColor del calendario, para que el
      // detalle y el evento del que se abre compartan color.
      if (this.isSubscription && this.booking.status !== 'PRE_SUBSCRIPTION') {
        return 'bookingSubscription';
      }
      if (this.booking.status === 'ACCEPTED') {
        return 'bookingAccepted';
      }
      if (this.booking.status === 'IN_USE') {
        return 'bookingInUse';
      }
      if (this.booking.status === 'REMOVED') {
        return 'bookingRemoved';
      }
      if (this.booking.status === 'CANCELLED') {
        return 'bookingReubicationCancelled';
      }
      if (this.booking.status === 'PAID') {
        return 'bookingPaid';
      }
      if (this.booking.type === 'SYSTEM') {
        return 'bookingSystem';
      }
      return 'secondary';
    },
    startFormatted() {
      return this.$options.filters.formatDateAsUtc(
        this.booking.startLocal,
        this.$i18n.locale
      );
    },
    endFormatted() {
      return this.$options.filters.formatDateAsUtc(
        this.booking.endLocal,
        this.$i18n.locale
      );
    },
    maxAvailabilityFormatted() {
      return this.$options.filters.formatDateAsUtc(
        this.booking.maxAvailabilityLocal,
        this.$i18n.locale
      );
    },
    closingText() {
      const locale = this.$i18n.locale;
      const closingTime = this.$options.filters.formatDateAsUtc(
        this.booking.closingTime,
        locale
      );
      if (!this.showReopeningTime) {
        return this.$i18n.t('reservation.warning.closing.time.noReopening', {
          closingTime: closingTime
        });
      }
      return this.$i18n.t('reservation.warning.closing.time', {
        closingTime: closingTime,
        reopeningTime: this.$options.filters.formatDateAsUtc(
          this.booking.reopeningTime,
          locale
        )
      });
    },
    isPricePaid() {
      // Los servicios se comportan como los abonos: lo que se muestra es el
      // importe cobrado, no una tarifa por hora.
      return (
        this.isSubscription ||
        this.booking.isFacility ||
        this.booking.status === 'PAID'
      );
    },
    bookingPrice() {
      // Mientras no este pagada, lo que se muestra es la tarifa, no el importe
      // final: sin el /h se lee como si la reserva costara eso en total.
      return this.isPricePaid
        ? this.booking.price
        : this.booking.hourPriceWithCurrency;
    },
    isAPR() {
      return this.booking.isAPR;
    },
    hasAssignation() {
      return this.booking.hasSpotAssignation;
    },
  },
  mounted() {
    let self = this;
    setTimeout(() => {
      self.isReady = true;
    }, 250);
  },
};
</script>

<style lang="scss" scoped>
.cardheight {
  height: 100%;
}
</style>
